/**
 * PaylasimliBelgeYukleModal.jsx — SGK Hizmet Dökümü (96) / İSG Eğitim Katılım Listesi (98) yükler ve
 * çalışanlara dağıtır. Backlog #142. İki yerden açılır: firma belgeleri sayfasındaki panelden
 * (PaylasimliBelgelerPanel) ve bir çalışanın sayfasındaki SGK/İSG kutusundan (o çalışan `onceden`
 * seçili gelir, kalan çalışanlar ayrıca eklenebilir).
 *
 * Bitiş tarihi PERSONEL için ZORUNLU (`staff`): 24 kişiyi kapsayan bir belge için +60 gün varsayılanı yanlış. FİRMA girişinde alan
 * HİÇ yok (#297, backend #295): firma yalnızca yükler, tarihi onaylayan personel girer; gönderilen tarih zaten yok sayılırdı.
 * Herkes Beklemede başlar — personel çalışan başına onaylar. "Önceki belgenin çalışanlarını da ekle" bir
 * önceki ayın listesini tek tıkla yeni belgeye taşır (sunucu tarafında; sonradan ayrılanlar atlanır).
 */
import React, { useState } from "react";
import { Alert, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import PersonelSecici from "../Kisi/PersonelSecici";
import TarihGirdisi from "../Common/TarihGirdisi";
import {
  PAYLASIMLI_TIPLER, bugunYmd, donemYaz, dosyaHatasi, uploadPaylasimliBelge,
} from "../../services/personelBelgeService";
import { useTranslation } from "react-i18next";

// WAS (until #297): const bugun = () => new Date().toISOString().slice(0, 10); — UTC günü, gece yarısı civarında bir gün geri kalıyordu.
const bugun = bugunYmd;
const trTarih = (iso) => (iso ? iso.split("-").reverse().join(".") : "—");

export default function PaylasimliBelgeYukleModal({
  firmaId,
  fileTypeId,
  staff = false,
  onceden = [],
  oncekiBelgeler = [],
  onClose,
  onDone,
}) {
  const { t: tx } = useTranslation("components");
  const tip = PAYLASIMLI_TIPLER.find((t) => t.fileTypeId === fileTypeId);
  const [file, setFile] = useState(null);
  const [bitis, setBitis] = useState("");
  const [ad, setAd] = useState("");
  const [personIds, setPersonIds] = useState(onceden.map(Number));
  const [kaynakId, setKaynakId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const kaynak = oncekiBelgeler.find((b) => String(b.belgeId) === String(kaynakId));
  const dosyaSorunu = file ? dosyaHatasi(file) : null;
  const bitisGecmis = bitis && bitis < bugun();
  // WAS (until #297): const gecerli = file && !dosyaSorunu && bitis && !bitisGecmis;
  const gecerli = file && !dosyaSorunu && (!staff || (bitis && !bitisGecmis));

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      const sonuc = await uploadPaylasimliBelge(firmaId, {
        file, fileTypeId, bitisTarihi: staff ? bitis : undefined, ad, personIds, kaynakBelgeId: kaynak?.belgeId ?? null,
      });
      onDone?.(sonuc);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen toggle={saving ? undefined : onClose} size="lg" scrollable>
      <ModalHeader toggle={saving ? undefined : onClose}>{tip?.ad ?? tx("paylasimliBelgeYukle.paylasimliBelge")}{" "}{tx("paylasimliBelgeYukle.yukle")}</ModalHeader>
      <ModalBody>
        <Alert fade={false} color="info" className="small py-2">
          {tx("paylasimliBelgeYukle.buBelge")}{" "}<strong>{tx("paylasimliBelgeYukle.firmaninBelgesidir")}</strong>{tx("paylasimliBelgeYukle.sectiginizHerCalisanin")}{" "}{tip?.slot === "isg" ? tx("paylasimliBelgeYukle.isg") : tx("paylasimliBelgeYukle.sgk")}{" "}{tx("paylasimliBelgeYukle.kutusunuDoldururTekBirKisiye")}
        </Alert>

        {error && <Alert fade={false} color="danger" className="py-2 small">{error}</Alert>}

        <FormGroup>
          <Label className="small mb-1">{tx("paylasimliBelgeYukle.dosya")}{" "}<span className="text-muted">{tx("paylasimliBelgeYukle.pdfJpgPngEnFazla")}</span></Label>
          <Input
            type="file" bsSize="sm" accept=".pdf,.jpg,.jpeg,.png" invalid={!!dosyaSorunu}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {dosyaSorunu && <div className="small text-danger mt-1">{dosyaSorunu}</div>}
        </FormGroup>

        <div className="row g-3">
          {staff && (
            <FormGroup className="col-md-5 mb-0">
              <Label className="small mb-1">{tx("paylasimliBelgeYukle.sonGecerlilikTarihi")}</Label>
              <TarihGirdisi min={bugun()} value={bitis} invalid={!!bitisGecmis} onChange={setBitis} />
              {bitisGecmis && <div className="small text-danger mt-1">{tx("paylasimliBelgeYukle.tarihGecmisteOlamaz")}</div>}
            </FormGroup>
          )}
          <FormGroup className={`${staff ? "col-md-7" : "col-12"} mb-0`}>
            <Label className="small mb-1">{tx("paylasimliBelgeYukle.belgeAdi")}{" "}<span className="text-muted">{tx("paylasimliBelgeYukle.bossaTurAdi")}</span></Label>
            <Input bsSize="sm" value={ad} maxLength={120} placeholder={tx("paylasimliBelgeYukle.ornMart2026HizmetDokumu")} onChange={(e) => setAd(e.target.value)} />
          </FormGroup>
        </div>

        {oncekiBelgeler.length > 0 && (
          <FormGroup className="mt-3 mb-0">
            <Label className="small mb-1">{tx("paylasimliBelgeYukle.oncekiBelgeninCalisanlariniDaEkle")}</Label>
            <Input type="select" bsSize="sm" value={kaynakId} onChange={(e) => setKaynakId(e.target.value)}>
              <option value="">{tx("common:hayir")}</option>
              {oncekiBelgeler.map((b) => (
                <option key={b.belgeId} value={b.belgeId}>
                  {tx("paylasimliBelgeYukle.kisi", { v: b.fileGivenName || b.belgeTuru, trTarih: trTarih(b.expiryDate), kisiSayisi: b.kisiSayisi })}
                  {b.belgeDonemi ? ` · ${donemYaz(b.belgeDonemi)}` : ""}
                </option>
              ))}
            </Input>
          </FormGroup>
        )}

        <hr />
        <div className="fw-semibold small mb-2">
          {tx("paylasimliBelgeYukle.calisanlar")}{" "}<span className="text-muted fw-normal">{tx("paylasimliBelgeYukle.istegeBagliSonradanDaEklenebilir")}</span>
        </div>
        <PersonelSecici firmaId={firmaId} secili={personIds} onChange={setPersonIds} />
        {kaynak && (
          <div className="small text-muted mt-2">
            {tx("paylasimliBelgeYukle.ayricaBelgesindekiCalisanEklenecek", { v: kaynak.fileGivenName || kaynak.belgeTuru, kisiSayisi: kaynak.kisiSayisi })}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <span className="small text-muted me-auto">
          {staff ? tx("paylasimliBelgeYukle.eklenenHerkesBeklemedeBaslar") : tx("paylasimliBelgeYukle.firmaBeklemedeFdsOnaylar")}
        </span>
        <Button color="light" onClick={onClose} disabled={saving}>{tx("paylasimliBelgeYukle.vazgec")}</Button>
        <Button color="primary" onClick={submit} disabled={!gecerli || saving}>
          {saving ? <Spinner size="sm" /> : tx("common:yukle")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
